import { trackDisclaimerModalOpen } from '../../../analytics/event-tracking/trackDisclaimerModalOpen';
import { ContentBrick } from '../../../app-kit/components/ContentBrick/ContentBrick';
import { DisclaimerIcon } from '../../../app-kit/components/DisclaimerIcon/DisclaimerIcon';
import { DynamicContent } from '../../../app-kit/components/DynamicContent/DynamicContent';
import type {
  ButtonFragment,
  TextFragment,
  WebLinkFragment,
} from '../../../app-kit/components/DynamicContent/helpers/mapDynamicContentItem';
import {
  Text,
  paletteHelper,
  spacingHelper,
  typographyHelper,
  mediaHelper,
} from '@property.com.au/ui-kit';
import { InternetConnectionBrick } from './subcomponents/InternetConnectionBrick/InternetConnectionBrick';
import { usePropertyPageModalContext } from '../../contexts/PropertyPageModalContext/PropertyPageModalContext';
import type { FunctionComponent, PropsWithChildren } from 'react';
import styled from 'styled-components';
import { mapDynamicContentItem } from '../../../app-kit/components/DynamicContent/helpers/mapDynamicContentItem';
import { usePropertyPageQuery } from '../../graphql/usePropertyPageQuery';
import qualityAdequatePng from '../../../assets/internet-availability/quality-adequate.png';
import qualityAmazingPng from '../../../assets/internet-availability/quality-amazing.png';
import qualityExcellentPng from '../../../assets/internet-availability/quality-excellent.png';
import qualityGoodPng from '../../../assets/internet-availability/quality-good.png';
import qualityLimitedPng from '../../../assets/internet-availability/quality-limited.png';
import qualityPoorPng from '../../../assets/internet-availability/quality-poor.png';
import { isNotNullOrUndefinedValue } from '../../../helpers/isNotNullOrUndefinedValue';

type ConnectionQuality = 'ADEQUATE' | 'AMAZING' | 'EXCELLENT' | 'GOOD' | 'LIMITED' | 'POOR';

const Description = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  > div:not(:empty) {
    margin-bottom: ${spacingHelper('large')};
  }
`;

const DisclaimerText = styled(Text)`
  ${typographyHelper('caption')};
  color: ${paletteHelper('textSecondary')};
  margin-top: ${spacingHelper('extraSmall')};
`;

const ConnectionQualityGaugeImage = styled.img`
  width: 100px;
  height: 100%;
  margin-right: ${spacingHelper('small')};
  margin-left: ${spacingHelper('twoExtraLarge')};
  margin-bottom: ${spacingHelper('large')};
`;

const ConnectionQualityMapper: Record<ConnectionQuality, string> = {
  ADEQUATE: qualityAdequatePng,
  AMAZING: qualityAmazingPng,
  EXCELLENT: qualityExcellentPng,
  GOOD: qualityGoodPng,
  LIMITED: qualityLimitedPng,
  POOR: qualityPoorPng,
};

interface ConnectionQualityGaugeProps {
  connectionQuality: ConnectionQuality;
}

export const ConnectionQualityGauge: FunctionComponent<ConnectionQualityGaugeProps> = ({
  connectionQuality,
}) => {
  return (
    <ConnectionQualityGaugeImage
      alt={`${connectionQuality.toLowerCase()}-connection-quality-logo`}
      src={ConnectionQualityMapper[connectionQuality]}
    />
  );
};

interface InternetAvailabilityBrickProps {
  title: string;
  subtitle: string;
  description: (WebLinkFragment | TextFragment | ButtonFragment)[];
  connectionQuality?: ConnectionQuality | null;
}

const InternetAvailabilityBrick: FunctionComponent<
  PropsWithChildren<InternetAvailabilityBrickProps>
> = ({ title, subtitle, description, connectionQuality, children }) => {
  const { displayModal } = usePropertyPageModalContext();

  return (
    <ContentBrick
      title={title}
      subtitle={subtitle}
      headerChildren={
        <DisclaimerIcon
          title="Open Internet Availability Disclaimer"
          onClick={() => {
            displayModal({ name: 'internetAvailabilityDisclaimer' });
            trackDisclaimerModalOpen({
              inPageSource: 'property_page__internet_availability_brick___disclaimer_link',
            });
          }}
        />
      }
    >
      <Description>
        <div>
          <DynamicContent
            dynamicContents={description
              .map(mapDynamicContentItem)
              .filter(isNotNullOrUndefinedValue)}
            textColorVariant="textSecondary"
          />
        </div>
        {connectionQuality && <ConnectionQualityGauge connectionQuality={connectionQuality} />}
      </Description>
      {children}
      <DisclaimerText>
        Always check with your preferred provider to see what options are available at this property
      </DisclaimerText>
    </ContentBrick>
  );
};

const InternetAvailabilitySubBrickContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: ${spacingHelper('extraSmall')};
`;

const StyledInternetConnectionBrick = styled(InternetConnectionBrick)`
  margin: unset;

  ${mediaHelper('desktopMin')} {
    flex-grow: 1;
    width: 0;
  }
`;

export const InternetAvailabilityBrickWithData: FunctionComponent = () => {
  const internetAvailability =
    usePropertyPageQuery()?.data.propertyPageBySlugV3.internetAvailability;
  if (!internetAvailability) return null;

  const { title, subtitle, description, connectionQuality, nbnConnection, mobileCoverage } =
    internetAvailability;

  return (
    <InternetAvailabilityBrick
      title={title}
      subtitle={subtitle}
      description={description}
      connectionQuality={connectionQuality}
    >
      <InternetAvailabilitySubBrickContainer>
        <StyledInternetConnectionBrick connection={nbnConnection} />
        <StyledInternetConnectionBrick connection={mobileCoverage} />
      </InternetAvailabilitySubBrickContainer>
    </InternetAvailabilityBrick>
  );
};
